import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EllipsisVertical, Share, SquarePlus } from 'lucide-react';
import { getScreenLockState, subscribeScreenLock } from '@/auth/screen-lock';
import { NexaGlyph } from '@/components/nexa-mark/nexa-glyph';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { touchScreen } from '@/lib/touch-screen';
import { enterFullscreen, fullscreenSupported, installedApp, isFullscreen } from './fullscreen';
import { installWayOf, useInstallOffer, type InstallWay } from './install';

// «Usa Nexa como app» — a moment after entering, on a touch screen (an iPad, an Android
// tablet, a phone) in the browser: how to add the console to the home screen, the way
// this device does it (PO 2026-11-01: «expliquémosle al usuario cómo hacerlo al ingresar
// y no se lo volvamos a repetir hasta después de un tiempo»). Opened from its icon, the
// console has no browser bars, typing included — full screen in the browser loses them
// to the keyboard (fullscreen.ts). Where Chrome offers its own install dialog, «Instalar
// Nexa» opens it; where the browser can, «Pantalla completa por ahora» bridges the gap.
// Shown once a week at most (counted from when it shows), never in the installed app,
// never over the screen lock. Storage is a per-viewer convenience: blocked, it asks.

/** How long after entering it asks — the screen drawn first. */
export const ASK_AFTER_MS = 1200;
/** Once shown, not again for a week. */
export const ASK_AGAIN_MS = 7 * 24 * 60 * 60 * 1000;
/** When it last showed (`localStorage`, epoch ms). */
export const ASKED_KEY = 'nexa.install.askedAt';

const STEP_ICONS: Readonly<Record<InstallWay, readonly [ReactNode, ReactNode]>> = {
  ios: [<Share className="h-4 w-4" />, <SquarePlus className="h-4 w-4" />],
  android: [
    <EllipsisVertical className="h-4 w-4" />,
    <SquarePlus className="h-4 w-4" />,
  ],
  other: [
    <EllipsisVertical className="h-4 w-4" />,
    <SquarePlus className="h-4 w-4" />,
  ],
};

function askedRecently(now: number): boolean {
  try {
    const at = Number(localStorage.getItem(ASKED_KEY));
    return at > 0 && now - at < ASK_AGAIN_MS;
  } catch {
    return false;
  }
}

function remember(now: number): void {
  try {
    localStorage.setItem(ASKED_KEY, String(now));
  } catch {
    // blocked storage: it asks again next time
  }
}

function shouldAsk(): boolean {
  return touchScreen() && !installedApp() && !askedRecently(Date.now());
}

export function InstallPrompt() {
  const { t } = useTranslation();
  const lock = useSyncExternalStore(subscribeScreenLock, getScreenLockState, getScreenLockState);
  const install = useInstallOffer();
  const [open, setOpen] = useState(false);
  const [way] = useState(installWayOf);

  useEffect(() => {
    if (!shouldAsk()) return;
    const id = setTimeout(() => {
      if (!shouldAsk()) return;
      remember(Date.now());
      setOpen(true);
    }, ASK_AFTER_MS);
    return () => clearTimeout(id);
  }, []);

  const steps: { icon: ReactNode; text: string }[] = [
    { icon: STEP_ICONS[way][0], text: t(`fullscreen.install.${way}1`) },
    { icon: STEP_ICONS[way][1], text: t(`fullscreen.install.${way}2`) },
    { icon: <NexaGlyph className="h-4 w-5" />, text: t(`fullscreen.install.${way}3`) },
  ];
  const canFullscreen = fullscreenSupported() && !isFullscreen();

  return (
    <Dialog open={open && !lock.locked} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="install-prompt" data-way={way}>
        <DialogHeader>
          <DialogTitle>{t('fullscreen.install.title')}</DialogTitle>
          <DialogDescription>{t('fullscreen.install.body')}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3" data-testid="install-steps">
          {steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <span
                aria-hidden
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground"
              >
                {step.icon}
              </span>
              <span className="pt-1.5">{step.text}</span>
            </li>
          ))}
        </ol>
        <p className="text-xs text-muted-foreground">{t('fullscreen.install.again')}</p>
        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:items-center">
          {canFullscreen ? (
            <Button
              variant="ghost"
              className="text-muted-foreground sm:mr-auto"
              onClick={() => {
                // in the tap itself: the browser refuses full screen without one
                void enterFullscreen();
                setOpen(false);
              }}
              data-testid="install-prompt-fullscreen"
            >
              {t('fullscreen.install.fullscreenNow')}
            </Button>
          ) : null}
          <Button
            variant={install ? 'outline' : 'default'}
            onClick={() => setOpen(false)}
            data-testid="install-prompt-ok"
          >
            {t('fullscreen.install.gotIt')}
          </Button>
          {install ? (
            <Button
              onClick={() => {
                void install();
                setOpen(false);
              }}
              data-testid="install-prompt-install"
            >
              {t('fullscreen.install.install')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
